import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Share2, Bookmark, X, Download, Lock, Plus, Pencil, Compass, MoreHorizontal } from 'lucide-react';
import { CalculatorTabs } from './calculator/CalculatorTabs';
import { LivePaymentBottomBar } from './calculator/LivePaymentBottomBar';
import { useCalculatorStore } from '../store/calculatorStore';
import { SignupModal } from './SignupModal';
import { CloseTabModal } from './CloseTabModal';
import { RenameTabModal } from './RenameTabModal';
import { ShareModal } from './ui/ShareModal';
import { PresetResetModal } from './PresetResetModal';
import { LoadSharedScenarioModal } from './LoadSharedScenarioModal';
import { SaveScenarioModal } from './SaveScenarioModal';
import { TourStartConfirmModal } from './TourStartConfirmModal';
import { copyTextToClipboard, generateShareUrl } from '../utils/shareUtils';
import { downloadMortgageExcel } from '../utils/exportUtils';
import { usePageMeta } from '../utils/seo';
import {
  fetchCountryFromIP,
  getResolvedDefaultCountry,
  isLocaleAmbiguous,
  setGeoDefaultFromIp,
} from '../utils/geoDefaultCountry';
import { getPresetTabs, type QuickStartPreset } from '../utils/presetMortgageTerms';
import { useTutorial } from './tutorial/useTutorial';
import { trackEvent } from '../utils/analytics';

const PRESET_LABELS: Record<QuickStartPreset, { label: string }> = {
  'first-time': { label: 'First-time buyer' },
  refinance: { label: 'Refinance existing' },
  investment: { label: 'Investment property' },
  custom: { label: 'Custom scenario' },
};

export const Calculator: React.FC = () => {
  usePageMeta({
    title: 'Mortgage Calculator & Comparison Tool | Compare Home Loans',
    description:
      'Use our free mortgage calculator to compare home loan options, interest rates, payments, and savings. Find the best mortgage for your needs.',
    path: '/',
  });

  const calculatorTabs = useCalculatorStore(s => s.calculatorTabs);
  const addCalculatorTab = useCalculatorStore(s => s.addCalculatorTab);
  const removeCalculatorTab = useCalculatorStore(s => s.removeCalculatorTab);
  const renameCalculatorTab = useCalculatorStore(s => s.renameCalculatorTab);
  const activeTabId = useCalculatorStore(s => s.activeTabId);
  const setActiveTabId = useCalculatorStore(s => s.setActiveTabId);
  const hasUnsavedChanges = useCalculatorStore(s => s.hasUnsavedChanges);
  const setHasUnsavedChanges = useCalculatorStore(s => s.setHasUnsavedChanges);
  const applyPresetToAllTabs = useCalculatorStore(s => s.applyPresetToAllTabs);
  const applyInitialGeoCountryToAllTabs = useCalculatorStore(s => s.applyInitialGeoCountryToAllTabs);
  const renameTargetId = useCalculatorStore(s => s.renameTargetId);
  const setRenameTargetId = useCalculatorStore(s => s.setRenameTargetId);
  const pendingShareState = useCalculatorStore(s => s.pendingShareState);
  const setPendingShareState = useCalculatorStore(s => s.setPendingShareState);
  const loadSharedState = useCalculatorStore(s => s.loadSharedState);

  const [searchParams, setSearchParams] = useSearchParams();
  const skipGeoIpRef = useRef(searchParams.has('s'));

  const [activePreset, setActivePreset] = useState<QuickStartPreset>('custom');
  const [pendingPreset, setPendingPreset] = useState<QuickStartPreset | null>(null);
  const [isPresetResetModalOpen, setIsPresetResetModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [closeTargetId, setCloseTargetId] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState('');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareClipboardAutoResult, setShareClipboardAutoResult] = useState<boolean | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [hasSavedOnce, setHasSavedOnce] = useState(false);
  const [tourStartConfirmOpen, setTourStartConfirmOpen] = useState(false);
  const [exportActionsMenuOpen, setExportActionsMenuOpen] = useState(false);
  const exportActionsMenuRef = useRef<HTMLDivElement>(null);

  const { startTutorial, destroyTutorial } = useTutorial();

  const ensureTourBaseline = useCallback(() => {
    const store = useCalculatorStore.getState();
    store.calculatorTabs.slice(2).forEach(tab => {
      store.removeCalculatorTab(tab.id);
    });
    while (useCalculatorStore.getState().calculatorTabs.length < 2) {
      useCalculatorStore.getState().addCalculatorTab();
    }
    const country = getResolvedDefaultCountry();
    useCalculatorStore.getState().applyPresetToAllTabs(getPresetTabs('custom', country));
    const firstId = useCalculatorStore.getState().calculatorTabs[0]!.id;
    useCalculatorStore.getState().setActiveTabId(firstId);
    setActivePreset('custom');
  }, []);

  const startTutorialRef = useRef(startTutorial);
  startTutorialRef.current = startTutorial;
  const ensureTourBaselineRef = useRef(ensureTourBaseline);
  ensureTourBaselineRef.current = ensureTourBaseline;

  useEffect(() => {
    if (searchParams.get('tour') !== '1') return;
    if (searchParams.has('s')) return;

    const timer = window.setTimeout(() => {
      ensureTourBaselineRef.current();
      startTutorialRef.current();
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete('tour');
          return next;
        },
        { replace: true },
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [searchParams, setSearchParams]);

  useEffect(() => () => destroyTutorial(), [destroyTutorial]);

  const handleTakeTourClick = () => {
    trackEvent('take_the_tour_click', {
      page_type: 'calculator',
      immediate_start: calculatorTabs.length >= 2,
    });
    if (calculatorTabs.length < 2) {
      setTourStartConfirmOpen(true);
      return;
    }
    startTutorial();
  };

  const handleTourStartConfirm = () => {
    ensureTourBaseline();
    setTourStartConfirmOpen(false);
    startTutorial();
  };

  const executePreset = (key: QuickStartPreset) => {
    // Remove any extra tabs beyond the first two before applying
    calculatorTabs.slice(2).forEach(tab => removeCalculatorTab(tab.id));
    setActivePreset(key);
    const country = getResolvedDefaultCountry();
    applyPresetToAllTabs(getPresetTabs(key, country));
  };

  useEffect(() => {
    if (skipGeoIpRef.current || !isLocaleAmbiguous()) return;

    let cancelled = false;
    void (async () => {
      const ipCountry = await fetchCountryFromIP();
      if (cancelled || ipCountry === null) return;
      if (useCalculatorStore.getState().hasUnsavedChanges) return;
      setGeoDefaultFromIp(ipCountry);
      applyInitialGeoCountryToAllTabs(ipCountry);
    })();

    return () => {
      cancelled = true;
    };
  }, [applyInitialGeoCountryToAllTabs]);

  const handlePreset = (key: QuickStartPreset) => {
    const needsWarning = hasUnsavedChanges || calculatorTabs.length > 2;
    if (needsWarning) {
      setPendingPreset(key);
      setIsPresetResetModalOpen(true);
      return;
    }
    executePreset(key);
  };

  const handlePresetResetConfirm = () => {
    if (pendingPreset) {
      executePreset(pendingPreset);
      setPendingPreset(null);
    }
    setIsPresetResetModalOpen(false);
  };

  const handlePresetResetCancel = () => {
    setPendingPreset(null);
    setIsPresetResetModalOpen(false);
  };

  const handleSharedScenarioConfirm = () => {
    if (pendingShareState) {
      loadSharedState(pendingShareState);
      setPendingShareState(null);
      setActivePreset('custom');
    }
  };

  const handleSharedScenarioCancel = () => {
    setPendingShareState(null);
  };

  const handleDownload = () => {
    void downloadMortgageExcel(calculatorTabs);
  };

  const handleShare = () => {
    const url = generateShareUrl(calculatorTabs, activeTabId);
    setShareUrl(url);
    setShareClipboardAutoResult(null);
    setIsShareModalOpen(true);
    void copyTextToClipboard(url).then(setShareClipboardAutoResult);
  };

  const handleSave = () => {
    const url = generateShareUrl(calculatorTabs, activeTabId);
    window.history.replaceState(null, '', url);
    setHasUnsavedChanges(false);
    setHasSavedOnce(true);
    setShowSaveModal(true);
  };

  const handleAddTab = () => {
    if (calculatorTabs.length < 3) {
      addCalculatorTab();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setIsSignupModalOpen(true);
    }
  };

  const closeTarget = closeTargetId ? calculatorTabs.find(t => t.id === closeTargetId) : null;
  const renameTarget = renameTargetId ? calculatorTabs.find(t => t.id === renameTargetId) : null;

  const handleConfirmClose = () => {
    if (closeTargetId) {
      removeCalculatorTab(closeTargetId);
      setCloseTargetId(null);
    }
  };

  const handleConfirmRename = (newTitle: string) => {
    if (renameTargetId) {
      renameCalculatorTab(renameTargetId, newTitle);
      setRenameTargetId(null);
    }
  };

  const prevTabIdRef = useRef<string>(activeTabId);
  useEffect(() => {
    const prev = prevTabIdRef.current;
    const next = activeTabId;
    if (prev !== next && (prev === 'compare' || next === 'compare')) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    prevTabIdRef.current = next;
  }, [activeTabId]);

  const tabScrollRef = useRef<HTMLDivElement>(null);
  const quickStartScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollQuickStartRight, setCanScrollQuickStartRight] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);

  useEffect(() => {
    const container = quickStartScrollRef.current;
    if (!container) return;

    const updateScrollState = () => {
      setCanScrollQuickStartRight(
        container.scrollLeft + container.clientWidth < container.scrollWidth - 1,
      );
    };

    container.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(container);
    updateScrollState();

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const container = tabScrollRef.current;
    if (!container) return;

    const updateScrollState = () => {
      setCanScrollTabsRight(
        container.scrollLeft + container.clientWidth < container.scrollWidth - 1,
      );
    };

    container.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(container);
    const inner = container.firstElementChild;
    if (inner) ro.observe(inner);
    updateScrollState();

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [calculatorTabs.length]);

  useEffect(() => {
    if (!exportActionsMenuOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExportActionsMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [exportActionsMenuOpen]);

  useEffect(() => {
    if (!exportActionsMenuOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!exportActionsMenuRef.current?.contains(event.target as Node)) {
        setExportActionsMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [exportActionsMenuOpen]);

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 639px)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hintShown = localStorage.getItem('mortgage-tab-swipe-hint-shown');
    if (!isMobile || prefersReducedMotion || hintShown) return;

    const timer = setTimeout(() => {
      const el = tabScrollRef.current;
      if (!el) return;
      el.scrollTo({ left: 80, behavior: 'smooth' });
      setTimeout(() => {
        el.scrollTo({ left: 0, behavior: 'smooth' });
        localStorage.setItem('mortgage-tab-swipe-hint-shown', '1');
      }, 700);
    }, 900);

    return () => clearTimeout(timer);
  }, []);

  const hasScenario2 = calculatorTabs.length >= 2;
  const isCompareActive = activeTabId === 'compare';


  return (
    <div
      className={`animate-fade-in mt-6 ${
        !isCompareActive ? 'pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-0' : ''
      }`}
    >
      {/* ── Hero Strip ── */}
      <div
        className="px-6 pt-5 pb-6 rounded-t-2xl"
        style={{ background: 'linear-gradient(135deg, #0F1F38 0%, #1A2F50 60%, #243A62 100%)' }}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-5">
          <div className="min-w-0">
            <h1 className="font-serif text-3xl md:text-4xl text-white leading-tight mb-1">
              Mortgage Compare Calculator
            </h1>
            <p className="text-navy-200 text-sm">
              Model any scenario, then compare side-by-side.{' '}
              <span className="md:hidden">
                <span className="text-gold-300 font-medium">Results update instantly</span>
                <span aria-hidden="true"> · </span>
                <button
                  type="button"
                  onClick={handleTakeTourClick}
                  className="text-navy-100 underline underline-offset-2 hover:text-gold-300 transition-colors"
                >
                  Take the tour
                </button>
              </span>
              <span className="hidden md:inline text-gold-300 font-medium">Results update instantly.</span>
            </p>
          </div>
          <button
            type="button"
            onClick={handleTakeTourClick}
            className="hidden md:inline-flex flex-shrink-0 items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border border-navy-600 bg-navy-800/50 text-navy-100 transition-all hover:border-gold-500 hover:text-gold-300"
          >
            <Compass size={18} className="text-gold-300" aria-hidden="true" />
            Take the Tour
          </button>
        </div>

        {/* Quick-start pills */}
        <div className="flex items-center gap-2 min-w-0 md:flex-wrap">
          <span className="text-navy-200 text-xs font-medium shrink-0 uppercase tracking-wide md:self-center md:mr-1">
            Quick start:
          </span>
          <div className="relative min-w-0 flex-1 md:flex-initial">
            {canScrollQuickStartRight && (
              <div
                className="pointer-events-none absolute inset-y-0 right-0 w-10 z-10 bg-gradient-to-r from-transparent to-navy-700 md:hidden"
                aria-hidden="true"
              />
            )}
            <div
              ref={quickStartScrollRef}
              data-tour="tour-quickstart-pills"
              className="flex flex-nowrap gap-2 overflow-x-auto scrollbar-none md:flex-wrap md:overflow-visible"
            >
            {(Object.entries(PRESET_LABELS) as [QuickStartPreset, { label: string }][]).map(([key, preset]) => (
              <button
                key={key}
                onClick={() => handlePreset(key)}
                className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  activePreset === key
                    ? 'bg-gold-500 border-gold-500 text-navy-900'
                    : 'bg-navy-800/50 border-navy-600 text-navy-100 hover:border-gold-500 hover:text-gold-300'
                }`}
              >
                {activePreset === key && (
                  <span className="w-1.5 h-1.5 rounded-full bg-navy-900 flex-shrink-0" />
                )}
                {preset.label}
              </button>
            ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Progress Tabs + Action Buttons ── */}
      <div
        id="calculator-tab-bar"
        className={`flex flex-col sticky top-16 z-40 border-b border-navy-700 ${isCompareActive ? '' : 'rounded-b-2xl'}`}
        style={{ background: '#1A2F50' }}
      >
        {/* Tab row + action buttons */}
        <div className="flex items-center justify-between px-6 pt-2">
          {/* Scenario tabs - rendered as folder tabs that sit on the panel below */}
          <div className="relative min-w-0 flex-1 self-end">
            {canScrollTabsRight && (
              <div
                className="pointer-events-none absolute inset-y-0 right-0 w-10 z-10 bg-gradient-to-r from-transparent to-navy-800 md:hidden"
                aria-hidden="true"
              />
            )}
            <div
              ref={tabScrollRef}
              className="flex items-end gap-1.5 overflow-x-auto scrollbar-none min-w-0"
            >
            <div
              data-tour="tour-scenario-tabs"
              className="flex items-end gap-1.5"
            >
              {calculatorTabs.map((tab, idx) => {
                const isActive = tab.id === activeTabId;
                return (
                  <div
                    key={tab.id}
                    data-tour={isActive ? 'tour-calculator-tab-active' : undefined}
                    className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap rounded-t-xl border-t-2 ${
                      isActive
                        ? 'bg-gold-500 text-navy-900 border-gold-500 border-l-2 border-r-2'
                        : 'bg-navy-700/60 text-navy-100 border-transparent border-l border-r border-navy-600/60 hover:bg-navy-700 hover:text-gold-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveTabId(tab.id)}
                      aria-current={isActive ? 'page' : undefined}
                      className={`flex items-center gap-2 px-4 pt-2.5 pb-3 rounded-t-xl focus:outline-none focus:ring-2 focus:ring-inset ${
                        isActive ? 'focus:ring-navy-900' : 'focus:ring-gold-400'
                      }`}
                    >
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          isActive ? 'bg-navy-900 text-gold-300' : 'bg-navy-600 text-navy-200'
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span>{tab.title}</span>
                      {isActive && (
                        <span className="hidden md:inline text-[10px] font-normal text-navy-900/70 ml-0.5">
                          • active
                        </span>
                      )}
                    </button>
                    {isActive && (
                      <span
                        data-tour="tour-scenario-tab-actions"
                        className="flex items-center gap-1 pr-2"
                      >
                        <button
                          type="button"
                          aria-label={`Rename ${tab.title}`}
                          className="min-w-7 min-h-7 inline-flex items-center justify-center rounded-md focus:outline-none focus:ring-2 focus:ring-navy-900 text-navy-900/70 hover:text-navy-900 transition-colors"
                          onClick={() => setRenameTargetId(tab.id)}
                        >
                          <Pencil size={12} aria-hidden="true" />
                        </button>
                        {calculatorTabs.length >= 2 && (
                          <button
                            type="button"
                            aria-label={`Close ${tab.title}`}
                            className="min-w-7 min-h-7 inline-flex items-center justify-center rounded-md focus:outline-none focus:ring-2 focus:ring-navy-900 text-navy-900/70 hover:text-red-600 transition-colors"
                            onClick={() => setCloseTargetId(tab.id)}
                          >
                            <X size={12} aria-hidden="true" />
                          </button>
                        )}
                      </span>
                    )}
                </div>
              );
              })}

              {/* + Add Tab button */}
              <button
                type="button"
                onClick={handleAddTab}
                title="Add new scenario tab"
                aria-label="Add new scenario tab"
                className="flex items-center justify-center px-3 pt-2.5 pb-3 rounded-t-xl bg-navy-700/40 text-navy-300 border-t-2 border-transparent border-l border-r border-navy-600/40 hover:bg-navy-700 hover:text-gold-300 transition-colors"
              >
                <Plus size={14} />
              </button>
            </div>

            {/* Vertical divider separating parallel scenario tabs from the Compare destination */}
            <div className="self-stretch mx-2 w-px bg-navy-600/60" aria-hidden="true" />

            {/* Compare tab - distinct gold-outlined treatment */}
            <button
              type="button"
              data-tour="tour-compare-tab"
              onClick={() => { if (hasScenario2) setActiveTabId('compare'); }}
              disabled={!hasScenario2}
              aria-label={!hasScenario2 ? 'Compare scenarios (add a second scenario to unlock)' : 'Compare scenarios'}
              className={`flex items-center gap-2 px-4 pt-2.5 pb-3 text-sm font-medium transition-colors whitespace-nowrap rounded-t-xl border-t-2 border-l-2 border-r-2 ${
                isCompareActive
                  ? 'bg-gold-500 text-navy-900 border-gold-500'
                  : !hasScenario2
                  ? 'bg-navy-800/60 text-navy-500 border-navy-700 cursor-not-allowed'
                  : 'bg-transparent text-gold-300 border-gold-500/60 hover:bg-gold-500/10 hover:border-gold-500'
              }`}
            >
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  isCompareActive
                    ? 'bg-navy-900 text-gold-300'
                    : !hasScenario2
                    ? 'bg-navy-700 text-navy-500'
                    : 'bg-gold-500/20 text-gold-300 border border-gold-500/40'
                }`}
              >
                {!hasScenario2 ? <Lock size={10} /> : calculatorTabs.length + 1}
              </span>
              <span>Compare</span>
              {isCompareActive && (
                <span className="hidden md:inline text-[10px] font-normal text-navy-900/70 ml-0.5">
                  • active
                </span>
              )}
            </button>
            </div>
          </div>

          {/* Save / Share / Download - desktop icons; mobile overflow menu */}
          <div
            ref={exportActionsMenuRef}
            data-tour="tour-calculator-export-actions"
            className="relative flex-shrink-0 ml-4 self-center"
          >
            <div className="hidden md:flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              aria-label="Save scenario"
              title="Save Scenario"
              className="flex items-center justify-center w-9 h-9 rounded-md text-sm font-medium bg-gold-500 text-navy-900 hover:bg-gold-400 transition-colors"
            >
              <Bookmark size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleShare}
              aria-label="Share scenario"
              title="Share"
              className="flex items-center justify-center w-9 h-9 rounded-md text-sm font-medium bg-gold-500 text-navy-900 hover:bg-gold-400 transition-colors"
            >
              <Share2 size={15} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleDownload}
              aria-label="Download Excel spreadsheet"
              title="Download Excel"
              className="flex items-center justify-center w-9 h-9 rounded-md text-sm font-medium bg-gold-500 text-navy-900 hover:bg-gold-400 transition-colors"
            >
              <Download size={15} aria-hidden="true" />
            </button>
            </div>

            <div className="md:hidden">
              <button
                type="button"
                onClick={() => setExportActionsMenuOpen(open => !open)}
                aria-expanded={exportActionsMenuOpen}
                aria-haspopup="menu"
                aria-label="More actions"
                className="flex items-center justify-center w-9 h-9 rounded-md text-navy-100 hover:text-gold-300 hover:bg-navy-700/60 transition-colors"
              >
                <MoreHorizontal size={20} aria-hidden="true" />
              </button>
              {exportActionsMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-50 mt-1 min-w-[11.5rem] rounded-lg border border-navy-600 bg-navy-800 py-1 shadow-panel"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setExportActionsMenuOpen(false);
                      handleSave();
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-navy-100 hover:bg-navy-700 hover:text-gold-300 transition-colors"
                  >
                    <Bookmark size={16} className="text-gold-400 shrink-0" aria-hidden="true" />
                    Save Scenario
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setExportActionsMenuOpen(false);
                      handleShare();
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-navy-100 hover:bg-navy-700 hover:text-gold-300 transition-colors"
                  >
                    <Share2 size={16} className="text-gold-400 shrink-0" aria-hidden="true" />
                    Share
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setExportActionsMenuOpen(false);
                      handleDownload();
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-navy-100 hover:bg-navy-700 hover:text-gold-300 transition-colors"
                  >
                    <Download size={16} className="text-gold-400 shrink-0" aria-hidden="true" />
                    Download Excel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Banners ── */}
      {hasSavedOnce && hasUnsavedChanges && (
        <div className="flex items-center gap-2 bg-gold-50 border-b border-gold-200 px-6 py-2.5 text-sm text-ink">
          <Bookmark size={15} className="flex-shrink-0 text-gold-600" />
          <span>
            Your calculations have changed since your last save. Click <strong>Save Scenario</strong> to update.
          </span>
        </div>
      )}

      {/* ── Calculator Content ── */}
      <CalculatorTabs
        onAddScenario={handleAddTab}
        hasScenario2={hasScenario2}
      />

      <LivePaymentBottomBar hasScenario2={hasScenario2} onAddScenario={handleAddTab} />

      {/* Modals */}
      <TourStartConfirmModal
        isOpen={tourStartConfirmOpen}
        onClose={() => setTourStartConfirmOpen(false)}
        onConfirm={handleTourStartConfirm}
      />
      <SaveScenarioModal isOpen={showSaveModal} onClose={() => setShowSaveModal(false)} />
      <SignupModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} />
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => {
          setIsShareModalOpen(false);
          setShareClipboardAutoResult(null);
        }}
        shareUrl={shareUrl}
        autoClipboardResult={shareClipboardAutoResult}
      />
      <CloseTabModal
        isOpen={!!closeTarget}
        tabTitle={closeTarget?.title ?? ''}
        onClose={() => setCloseTargetId(null)}
        onConfirm={handleConfirmClose}
      />
      <RenameTabModal
        isOpen={!!renameTarget}
        currentTitle={renameTarget?.title ?? ''}
        existingTitles={calculatorTabs.map(t => t.title)}
        onClose={() => setRenameTargetId(null)}
        onConfirm={handleConfirmRename}
      />
      <PresetResetModal
        isOpen={isPresetResetModalOpen}
        presetLabel={pendingPreset ? PRESET_LABELS[pendingPreset].label : ''}
        hasExtraTab={calculatorTabs.length > 2}
        onClose={handlePresetResetCancel}
        onConfirm={handlePresetResetConfirm}
      />
      <LoadSharedScenarioModal
        isOpen={pendingShareState !== null}
        hasExtraTab={calculatorTabs.length > 2}
        onClose={handleSharedScenarioCancel}
        onConfirm={handleSharedScenarioConfirm}
      />

    </div>
  );
};
